import React from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";

// Root component props
interface CollapsibleRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

// Trigger component props
interface CollapsibleTriggerProps {
  className?: string;
  children: React.ReactNode;
}

// Content component props
interface CollapsibleContentProps {
  className?: string;
  children: React.ReactNode;
}

/**
 * Collapsible component wrapping Radix UI Collapsible primitive.
 * Used for expandable/collapsible panels in the device sidebar.
 *
 * Features:
 * - Fixed 170px width trigger with text truncation
 * - Dark theme styling (grey-200 text on dark grey bg)
 * - Supports both controlled and uncontrolled modes
 * - Full keyboard navigation
 */
const CollapsibleRoot = React.forwardRef<HTMLDivElement, CollapsibleRootProps>(
  (
    { open, defaultOpen, onOpenChange, disabled, className, children },
    forwardedRef
  ) => {
    return (
      <CollapsiblePrimitive.Collapsible
        ref={forwardedRef}
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        disabled={disabled}
        className={`${className || ""}`}
      >
        {children}
      </CollapsiblePrimitive.Collapsible>
    );
  }
);

CollapsibleRoot.displayName = "Collapsible";

const CollapsibleTrigger = React.forwardRef<
  HTMLButtonElement,
  CollapsibleTriggerProps
>(({ className, children }, forwardedRef) => {
  return (
    <CollapsiblePrimitive.Trigger
      ref={forwardedRef}
      className={`w-full px-4 py-3 text-left text-gray-200 hover:bg-gray-800/50 transition-colors truncate focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset ${
        className || ""
      }`}
    >
      {children}
    </CollapsiblePrimitive.Trigger>
  );
});

CollapsibleTrigger.displayName = "CollapsibleTrigger";

const CollapsibleContent = React.forwardRef<
  HTMLDivElement,
  CollapsibleContentProps
>(({ className, children }, forwardedRef) => {
  return (
    <CollapsiblePrimitive.Content
      ref={forwardedRef}
      className={`overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down ${
        className || ""
      }`}
    >
      {children}
    </CollapsiblePrimitive.Content>
  );
});

CollapsibleContent.displayName = "CollapsibleContent";

// Compound component export
export const Collapsible = Object.assign(CollapsibleRoot, {
  Trigger: CollapsibleTrigger,
  Content: CollapsibleContent,
});

export default Collapsible;
